/**
 * React Query hooks — wraps PowerBIApiCall functions with caching,
 * deduplication, and automatic background refresh.
 *
 * These hooks replace manual useEffect + useState patterns across
 * Dashboard, PortcoDetail, and other pages.
 */

import { useQuery, useQueries } from "@tanstack/react-query";
import {
  getPortcos,
  getPortcoMetrics,
  getPortcoWeeklyTrend,
  getPortcoMonthlyTrend,
  getPortcoForecastAccuracy,
  getPortcoInventoryTrend,
  getOrderDetail,
  getPortcoNetSuiteData,
  getEcommerceBrands,
  getEcommerceOverview,
  getEcommerceRevenueTrend,
  getEcommerceProducts,
  getEcommerceCustomers,
  getEcommerceTraffic,
} from "../utils/PowerBIApiCall";
import type {
  PortcoListItem,
  PortcoMetrics,
  WeeklyTrendPoint,
  MonthlyTrendPoint,
  ForecastAccuracyData,
  InventoryTrendPoint,
  OrderDetailResponse,
  NetSuiteDataResponse,
  EcommerceBrand,
  EcommerceOverview,
  MonthlyRevenue,
  ProductABC,
  CohortRow,
  RefundRow,
  TrafficRow,
} from "../types/api";

// ============================================================================
// Portco Queries
// ============================================================================

export function usePortcos(enabled = true) {
  return useQuery<PortcoListItem[]>({
    queryKey: ["portcos"],
    queryFn: getPortcos,
    enabled,
  });
}

export function usePortcoMetrics(
  portcoId: string | undefined,
  enabled = true,
  reportDate?: string,
) {
  return useQuery<PortcoMetrics>({
    queryKey: ["portcoMetrics", portcoId, reportDate ?? null],
    queryFn: () => getPortcoMetrics(portcoId!, reportDate),
    enabled: enabled && !!portcoId,
  });
}

export function useWeeklyTrend(portcoId: string | undefined, enabled = true) {
  return useQuery<WeeklyTrendPoint[] | null>({
    queryKey: ["weeklyTrend", portcoId],
    queryFn: () => getPortcoWeeklyTrend(portcoId!),
    enabled: enabled && !!portcoId,
  });
}

export function useMonthlyTrend(portcoId: string | undefined, enabled = true) {
  return useQuery<MonthlyTrendPoint[] | null>({
    queryKey: ["monthlyTrend", portcoId],
    queryFn: () => getPortcoMonthlyTrend(portcoId!),
    enabled: enabled && !!portcoId,
  });
}

export function useForecastAccuracy(
  portcoId: string | undefined,
  enabled = true,
) {
  return useQuery<ForecastAccuracyData | null>({
    queryKey: ["forecastAccuracy", portcoId],
    queryFn: () => getPortcoForecastAccuracy(portcoId!),
    enabled: enabled && !!portcoId,
  });
}

export function useInventoryTrend(
  portcoId: string | undefined,
  enabled = true,
) {
  return useQuery<InventoryTrendPoint[] | null>({
    queryKey: ["inventoryTrend", portcoId],
    queryFn: () => getPortcoInventoryTrend(portcoId!),
    enabled: enabled && !!portcoId,
  });
}

export function useOrderDetail(portcoId: string | undefined, enabled = true) {
  return useQuery<OrderDetailResponse | null>({
    queryKey: ["orderDetail", portcoId],
    queryFn: () => getOrderDetail(portcoId!),
    enabled: enabled && !!portcoId,
  });
}

/**
 * Loads all portco data in parallel — replaces usePortcoData hook
 * with proper caching and deduplication.
 *
 * `reportDate` (optional, YYYY-MM-DD): when set, the core metrics query
 * fetches the dashboard "as of" that date. Aggregate trend queries
 * (weekly / monthly / inventory) intentionally ignore the override and
 * keep showing the latest data.
 */
export function usePortcoAllData(
  portcoId: string | undefined,
  enabled = true,
  reportDate?: string,
) {
  const metrics = usePortcoMetrics(portcoId, enabled, reportDate);
  const weeklyTrend = useWeeklyTrend(portcoId, enabled);
  const monthlyTrend = useMonthlyTrend(portcoId, enabled);
  const forecastAccuracy = useForecastAccuracy(portcoId, enabled);
  const inventoryTrend = useInventoryTrend(portcoId, enabled);

  const loading =
    metrics.isLoading ||
    weeklyTrend.isLoading ||
    monthlyTrend.isLoading ||
    forecastAccuracy.isLoading ||
    inventoryTrend.isLoading;

  const error = metrics.error
    ? metrics.error instanceof Error
      ? metrics.error.message
      : "Failed to load metrics"
    : null;

  return {
    metrics: metrics.data ?? null,
    weeklyTrend: weeklyTrend.data ?? null,
    monthlyTrend: monthlyTrend.data ?? null,
    forecastAccuracy: forecastAccuracy.data ?? null,
    inventoryTrend: inventoryTrend.data ?? null,
    loading,
    error,
  };
}

// ============================================================================
// Dashboard — loads all portco metrics in parallel, tracked per-portco so
// real "N of M loaded" progress can be shown instead of one atomic flag.
// ============================================================================

export function useDashboardData(enabled = true) {
  const portcosQuery = usePortcos(enabled);
  const portcos = portcosQuery.data ?? [];

  const metricsResults = useQueries({
    queries: portcos.map((p) => ({
      queryKey: ["dashboardMetricsItem", p.id],
      // Swallow per-portco failures here (matches prior Promise.all try/catch)
      // so one bad portco doesn't surface as a page-level error or block others.
      queryFn: () => getPortcoMetrics(p.id).catch(() => null),
      enabled: enabled && portcosQuery.isSuccess,
    })),
  });

  const metrics: Record<string, PortcoMetrics> = {};
  // Ids whose query has settled (success or swallowed failure) — distinct from
  // `metrics` (which only has the successful ones) so the UI can tell "still
  // loading" apart from "loaded but empty" for a given portco's card slot.
  const settledIds: string[] = [];
  metricsResults.forEach((r, i) => {
    // isPending (not isLoading = isPending && isFetching) so a disabled query
    // correctly stays "not settled" instead of reading as instantly complete.
    if (!r.isPending) settledIds.push(portcos[i].id);
    if (r.data) metrics[portcos[i].id] = r.data;
  });
  const total = portcos.length;
  const loaded = settledIds.length;

  return {
    portcos,
    metrics,
    loading: portcosQuery.isLoading || (total > 0 && loaded < total),
    progress: { loaded, total },
    settledIds,
    error:
      portcosQuery.error instanceof Error ? portcosQuery.error.message : null,
  };
}

// ============================================================================
// NetSuite
// ============================================================================

export function useNetSuiteData(portcoId: string | undefined, enabled = true) {
  return useQuery<NetSuiteDataResponse>({
    queryKey: ["netsuiteData", portcoId],
    queryFn: () => getPortcoNetSuiteData(portcoId!),
    enabled: enabled && !!portcoId,
  });
}

// ============================================================================
// E-commerce
// ============================================================================

export function useEcommerceBrands(enabled = true) {
  return useQuery<EcommerceBrand[]>({
    queryKey: ["ecommerceBrands"],
    queryFn: getEcommerceBrands,
    enabled,
  });
}

export function useEcommerceOverview(
  brandCode: string | undefined,
  enabled = true,
) {
  return useQuery<EcommerceOverview>({
    queryKey: ["ecommerceOverview", brandCode],
    queryFn: () => getEcommerceOverview(brandCode!),
    enabled: enabled && !!brandCode,
  });
}

export function useEcommerceRevenueTrend(
  brandCode: string | undefined,
  enabled = true,
) {
  return useQuery<MonthlyRevenue[]>({
    queryKey: ["ecommerceRevenueTrend", brandCode],
    queryFn: () => getEcommerceRevenueTrend(brandCode!),
    enabled: enabled && !!brandCode,
  });
}

export function useEcommerceProducts(
  brandCode: string | undefined,
  enabled = true,
) {
  return useQuery<ProductABC[]>({
    queryKey: ["ecommerceProducts", brandCode],
    queryFn: () => getEcommerceProducts(brandCode!),
    enabled: enabled && !!brandCode,
  });
}

export function useEcommerceCustomers(
  brandCode: string | undefined,
  enabled = true,
) {
  return useQuery<{ cohorts: CohortRow[]; refunds: RefundRow[] }>({
    queryKey: ["ecommerceCustomers", brandCode],
    queryFn: () => getEcommerceCustomers(brandCode!),
    enabled: enabled && !!brandCode,
  });
}

export function useEcommerceTraffic(
  brandCode: string | undefined,
  enabled = true,
) {
  return useQuery<TrafficRow[]>({
    queryKey: ["ecommerceTraffic", brandCode],
    queryFn: () => getEcommerceTraffic(brandCode!),
    enabled: enabled && !!brandCode,
  });
}
